import { ColumnDef } from '@applift/datagrid'
import { CmpConversionSelectItem } from '../../models/Conversion'
import { ConversionFilter, conversionFilterMap } from '../../models/List'
import { TextCell } from '../cellTypes/TextCell'
import { ConversionNameCell } from '../cellTypes/ConversionNameCell'
import { MeasurementPartnerCell } from '../cellTypes/MeasurementPartnerCell'
import { StatusCell } from '../cellTypes/StatusCell'
import { DateCell } from '../cellTypes/DateCell'
import { conversionStatusMap } from '../../models/Status'
import { PostbackPartner } from '../../models/Postback'

interface ColumnsMapperParams {
  conversionType: Omit<ConversionFilter, 'all'>
  postbackPartnerMapper: { [key: string]: PostbackPartner }
}

type ColumnsMapperReturn = {
  [key in keyof CmpConversionSelectItem]?: ColumnDef<
    CmpConversionSelectItem,
    any
  >
}

export const getColumnsMapper = ({
  conversionType,
  postbackPartnerMapper,
}: ColumnsMapperParams): ColumnsMapperReturn => {
  const mapper: ColumnsMapperReturn = {
    id: {
      accessorKey: 'id',
      header: 'ID',
      cell: props => {
        return <TextCell cellData={props.renderValue() as any} />
      },
      size: 90,
    },
    name: {
      accessorKey: 'name',
      header: `${
        conversionType?.toLowerCase() === 'pixel' ? 'Pixel' : 'Postback'
      } Name`,
      cell: props => {
        return (
          <ConversionNameCell
            value={props.renderValue() as string}
            conversionType={
              (conversionFilterMap[`${props.row.original.typeId}`] ||
                'pixel') as string
            }
          />
        )
      },
      size: 270,
    },
    created: {
      accessorKey: 'created',
      header: 'Created',
      cell: props => {
        return <DateCell cellData={props.renderValue() as number} />
      },
      size: 130,
    },
  }
  if (conversionType?.toLowerCase() === 'pixel') {
    mapper['statusId'] = {
      accessorKey: 'statusId',
      header: 'Status',
      cell: props => {
        return (
          <StatusCell
            conversion={{
              ...props.row.original,
              status: props.row.original.statusId
                ? conversionStatusMap[`${props.row.original.statusId}`]
                : undefined,
            }}
          />
        )
      },
      size: 150,
      enableSorting: false,
    }
  }
  if (conversionType?.toLowerCase() === 'postback') {
    mapper['postbackPartnerId'] = {
      accessorKey: 'postbackPartnerId',
      header: 'Measurement Partner',
      cell: props => {
        const { postbackPartnerId } = props.row.original
        const { name = null, logoUrl = undefined } = postbackPartnerId
          ? postbackPartnerMapper[postbackPartnerId] || {}
          : {}
        return <MeasurementPartnerCell value={name} logoUrl={logoUrl} />
      },
      size: 180,
      enableSorting: false,
    }
  }

  return mapper
}

const PIXEL_COLS = ['__check__', 'id', 'name', 'statusId', 'created']

const POSTBACK_COLS = [
  '__check__',
  'id',
  'name',
  'postbackPartnerId',
  'created',
]

export const getColumnsOrder = (
  conversionType: Omit<ConversionFilter, 'all'>
) => {
  switch (conversionType?.toLowerCase()) {
    case 'pixel':
      return PIXEL_COLS
    case 'postback':
      return POSTBACK_COLS
    default:
      return PIXEL_COLS
  }
}

export const getColumns = (
  postbackPartnerMapper: { [key: string]: PostbackPartner },
  conversionType?: Omit<ConversionFilter, 'all'>
): ColumnDef<CmpConversionSelectItem, any>[] => {
  if (!conversionType) {
    return []
  }
  const mapper = getColumnsMapper({ conversionType, postbackPartnerMapper })
  switch (conversionType.toLowerCase()) {
    case 'postback':
      return (POSTBACK_COLS.slice(1) as (keyof CmpConversionSelectItem)[]).map(
        (col: keyof ColumnsMapperReturn) =>
          mapper[col as keyof ColumnsMapperReturn]
      ) as any
    default:
      return (PIXEL_COLS.slice(1) as (keyof CmpConversionSelectItem)[]).map(
        (col: string) => mapper[col as keyof ColumnsMapperReturn]
      ) as any
  }
}
